/*  MUI  */
import { Box, Button, Paper, TableCell } from '@mui/material';

/*  Shared  */
import { DateFormat } from 'application/Shared/Components/Core';
import { KebabMenu } from 'application/Shared/Components/Menu/KebabMenu';
import { ManagedTable } from 'application/Shared/Components/Table';

/*  Modals  */
import { Confirm } from 'application/Shared/Modals/Confirm';
import { debug } from 'application/Shared/Utils';

/* Hooks */
import { useDeletePublisher } from 'hooks/dataAccess/Publisher/publisher';

/*  React  */
import React, { useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

/*  Modals  */
import { CreatePublisherModal } from '../Applications/Modals/CreatePublisherModal';

const headers = (t) => [
    {
        align: 'left',
        field: 'name',
        fieldType: 'string',
        renderHeader: <strong>{t('Common.name')}</strong>,
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: 'name',
        width: '20%'
    },
    {
        align: 'left',
        field: 'id',
        fieldType: 'string',
        renderHeader: <strong>{t('Common.id')}</strong>,
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: 'id',
        width: '20%'
    },
    {
        align: 'left',
        field: 'description',
        fieldType: 'string',
        renderHeader: <strong>{t('Common.description')}</strong>,
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: 'description',
        width: '35%'
    },
    {
        align: 'left',
        field: 'dateCreated',
        fieldType: 'date',
        renderHeader: <strong>{t('Common.dateAdded')}</strong>,
        sortable: true,
        sortableFieldType: 'date',
        sortableKey: 'dateCreated',
        width: '20%'
    },
    {
        align: 'right',
        renderHeader: <strong>{t('Common.actions')}</strong>,
        width: '20%'
    }
];

const deleteFn = async ({
    deletePublisher,
    id,
    setPromptDelete,
    setRerender
}) => {
    const success = await deletePublisher({ id });
    setPromptDelete(false);
    if (success) {
        setRerender(true);
    }
};

const PublisherTableRow = ({
    deletePublisher,
    row,
    setEditingRow,
    setOpenModal,
    setRerender
}) => {
    const { t } = useTranslation();
    const { name, id, description, dateCreated } = row;
    const [promptDelete, setPromptDelete] = useState(false);

    const onClick = (value) => {
        if (value === 'editPublisher') {
            setEditingRow(row);
            setOpenModal(true);
        }
        if (value === 'deletePublisher') {
            setPromptDelete(true);
        }
    };

    const options = [
        {
            key: 'editPublisherButton',
            label: t('GlobalAdmin.Publisher.editPublisher'),
            value: 'editPublisher'
        },
        {
            key: 'deletePublisherButton',
            label: t('GlobalAdmin.Publisher.deletePublisher'),
            value: 'deletePublisher'
        }
    ];

    return (
        <>
            <TableCell>{name}</TableCell>
            <TableCell>{id}</TableCell>
            <TableCell>{description}</TableCell>
            <TableCell>
                <DateFormat date={dateCreated} noTz />
            </TableCell>
            <TableCell>
                <Box display="flex" justifyContent="flex-end" my="8px">
                    <KebabMenu
                        data-testid="groupTableMenuButton"
                        onClick={onClick}
                        options={options}
                    />
                </Box>
            </TableCell>
            {promptDelete && (
                <Confirm
                    modalOpen={!!promptDelete}
                    promptConfirmFn={() =>
                        deleteFn({
                            deletePublisher,
                            id,
                            setPromptDelete,
                            setRerender
                        })
                    }
                    promptCancelFn={() => setPromptDelete(false)}
                    promptTitle={t('GlobalAdmin.Publisher.areYouSure')}
                    promptText={t('GlobalAdmin.Publisher.deleteModalMessage', {
                        publisherName: name
                    })}
                />
            )}
        </>
    );
};

const searchFields = ['name', 'id', 'description', 'dateCreated'];

export const GlobalPublisher = () => {
    const { t } = useTranslation();
    const { deletePublisher } = useDeletePublisher(t);
    const [openModal, setOpenModal] = useState(false);
    const [editingRow, setEditingRow] = useState(null);
    const [rerender, setRerender] = useState(false);

    const tableHeaders = useMemo(() => headers(t), [t]);

    const csvFields = [
        { key: 'name', label: t('Common.name') },
        { key: 'id', label: t('Common.id') },
        { key: 'description', label: t('Common.description') },
        { key: 'dateCreated', label: t('Common.dateCreated') }
    ];

    useLayoutEffect(() => {
        if (rerender) {
            debug.dev('Rerendering Publisher Table.');
            setRerender(false);
        }
    }, [rerender]);

    return (
        <>
            <Paper elevation={0} variant="outlined">
                <Box display="flex" justifyContent="flex-end" my="8px">
                    <Button
                        data-trackid="globalAdmin_publisher_modal_open"
                        onClick={() => setOpenModal(true)}
                        color="primary"
                        variant="contained"
                    >
                        {t('GlobalAdmin.Publisher.addPublisher')}
                    </Button>
                </Box>
                <ManagedTable
                    csvExport
                    csvExportFields={csvFields}
                    csvExportFileName="global_publisher"
                    headers={tableHeaders}
                    renderBody={(row) => (
                        <PublisherTableRow
                            deletePublisher={deletePublisher}
                            row={row}
                            setRerender={setRerender}
                            setOpenModal={setOpenModal}
                            setEditingRow={setEditingRow}
                        />
                    )}
                    renderBodyKey="id"
                    renderFooter
                    rerender={rerender}
                    rowsApi="/publisher"
                    rowsApiPreload
                    searchEnabled
                    sortDirection="DESC"
                    sortKey="name"
                    searchFields={searchFields}
                />
            </Paper>
            {openModal && (
                <CreatePublisherModal
                    open={openModal}
                    onClose={() => {
                        setOpenModal(false);
                    }}
                    onSuccessFn={() => setRerender(true)}
                    editingRow={editingRow}
                />
            )}
        </>
    );
};
